/* eslint-disable react/require-default-props */
import { Trans } from '@lingui/macro';
import moment from 'moment';
import 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import Datetime from 'react-datetime';

import { ListDropdown } from '@components/library/dropdown';
import Label from '@components/library/labels/PrimaryLabel';
import { getMomentLocale } from '@helpers/locale';

import './styles/base_styles.scss';

const defaultTimezone = moment.tz.guess() || 'Europe/London';
const timezoneOptions = moment.tz.names().map((name) => ({ value: name, label: name }));
const momentLocale = getMomentLocale();

const initialTimestamp = (timestamp) => {
  // return default if falsey
  if (!timestamp) {
    return moment().locale(momentLocale);
  }

  // check if it's a moment object
  if (moment.isMoment(timestamp)) {
    return timestamp.locale(momentLocale);
  }

  // check if it's a string or a native date object, e.g. new Date()
  if (typeof timestamp === 'string' || typeof timestamp.getMonth === 'function') {
    return moment(timestamp).isValid()
      ? moment(timestamp).locale(momentLocale)
      : timestamp;
  }

  return timestamp;
};

const DateTimePicker = ({
  allowPastDates,
  className,
  maxDaysInFuture,
  onChange,
  onDateValidityChange,
  showTime,
  showTimezones,
  timestamp,
}) => {
  const [displayDateError, setDisplayDateError] = useState(false);
  const [displayTimeError, setDisplayTimeError] = useState(false);
  const [selectedTimestamp, setSelectedTimestamp] = useState();
  const [selectedTimezone, setSelectedTimezone] = useState(defaultTimezone);

  // Define IDs as constants
  const dateSelectorId = 'date-selector';
  const timeSelectorId = 'time-selector';
  const timezoneSelectorId = 'timezone-selector';

  useEffect(() => {
    setSelectedTimestamp(initialTimestamp(timestamp));
  }, []);

  // Add accessibility attributes to calendar navigation buttons
  useEffect(() => {
    const enhanceCalendarButtons = () => {
      // Find all calendar pickers in the document (react-datetime renders them globally)
      const allPickers = document.querySelectorAll('.rdtPicker');

      allPickers.forEach((picker) => {
        const prevButton = picker.querySelector('.rdtPrev');
        const nextButton = picker.querySelector('.rdtNext');

        if (prevButton && !prevButton.getAttribute('role')) {
          prevButton.setAttribute('role', 'button');
          prevButton.setAttribute('aria-label', 'Previous month');
          prevButton.setAttribute('tabindex', '0');
        }

        if (nextButton && !nextButton.getAttribute('role')) {
          nextButton.setAttribute('role', 'button');
          nextButton.setAttribute('aria-label', 'Next month');
          nextButton.setAttribute('tabindex', '0');
        }
      });
    };

    // Enhance buttons when inputs are focused (calendar opens)
    const handleInputFocus = () => {
      // Use setTimeout to allow react-datetime to render the calendar
      setTimeout(enhanceCalendarButtons, 50);
    };

    // Add focus listeners to date/time inputs
    const dateInput = document.getElementById(dateSelectorId);
    const timeInput = showTime ? document.getElementById(timeSelectorId) : null;

    if (dateInput) {
      dateInput.addEventListener('focus', handleInputFocus);
    }
    if (timeInput) {
      timeInput.addEventListener('focus', handleInputFocus);
    }

    // Use MutationObserver to watch for calendar picker appearance
    const observer = new MutationObserver(() => {
      enhanceCalendarButtons();
    });

    // Observe the document body for calendar picker additions
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Also enhance on initial render
    enhanceCalendarButtons();

    // Cleanup
    return () => {
      observer.disconnect();
      if (dateInput) {
        dateInput.removeEventListener('focus', handleInputFocus);
      }
      if (timeInput) {
        timeInput.removeEventListener('focus', handleInputFocus);
      }
    };
  }, [dateSelectorId, timeSelectorId, showTime]);

  const generatePickerDateValidationFunction = () => {
    const yesterday = moment().subtract(1, 'day');
    const maxDate = maxDaysInFuture ? moment().add(maxDaysInFuture, 'days') : null;

    return (current) => {
      if (allowPastDates && !maxDate) return true;

      const isAfterMin = allowPastDates || current.isAfter(yesterday);
      const isBeforeMax = !maxDate || current.isBefore(maxDate, 'day') || current.isSame(maxDate, 'day');

      return isAfterMin && isBeforeMax;
    };
  };

  const checkValidDate = (date) => {
    const parsedDate = !moment.isMoment(date) ? moment(date, "MM/DD/YYYY", true) : date; // parse the input date string
    const currentDate = moment();
    const maxDate = maxDaysInFuture ? moment().add(maxDaysInFuture, 'days') : null;

    if (!parsedDate.isValid() ||
        (!allowPastDates && parsedDate.isBefore(currentDate, 'day')) ||
        (maxDate && parsedDate.isAfter(maxDate, 'day'))) {
      setDisplayDateError(true);
      onDateValidityChange(false); // Notify parent about invalid date
      return false;
    }
    setDisplayDateError(false);
    setDisplayTimeError(false);
    onDateValidityChange(true);
    return true;
  };

  const checkValidTime = (time) => {
    if ((typeof time === 'string' || !time.isValid())) {
      setDisplayTimeError(true);
      onDateValidityChange(false); // Notify parent about invalid date
      return false;
    }
    setDisplayTimeError(false);
    setDisplayDateError(false);
    onDateValidityChange(true);
    return true;
  }

  const handleUpdateTime = (time) => {
    if(!checkValidTime(time)) {
      return;
    }

    setSelectedTimestamp((prevState) => (
      moment([
        prevState.year(),
        prevState.month(),
        prevState.date(),
        time.hour(),
        time.minute(),
      ]).locale(momentLocale)
    ));
  };

  const handleUpdateDate = (date) => {
    if(!checkValidDate(date)) {
      return;
    }

    setSelectedTimestamp((prevState) => (
      moment([
        date.year(),
        date.month(),
        date.date(),
        prevState.hour(),
        prevState.minute(),
      ]).locale(momentLocale)
    ));
  };

  const handleBlurDate = (value) => {
    if (typeof value === 'string') {
      handleUpdateDate(moment(value));
    }
  };

  const handleBlurTime = (value) => {
    if (typeof value === 'string') {
      handleUpdateTime(moment(value));
    }
  };

  const formatTimestamp = (rawTimestamp) => {
    // formatting this is finicky, these steps and details are important
    const timeString = rawTimestamp.format('YYYY-MM-DD HH:mm'); // create a string- string is independent of timezone
    return moment.tz(timeString, selectedTimezone).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'); // e.g. 2013-11-18T03:55:00.000Z, create a new time in selected timezone
  };

  useEffect(() => {
    if (selectedTimestamp) {
      onChange(formatTimestamp(selectedTimestamp));
    }
  }, [selectedTimestamp, selectedTimezone]);

  return (
    <div className={`date-time-picker ${className}`}>
      <div className="date-time-picker__date date-time-picker__container">
        <Label attributes={{ htmlFor: dateSelectorId }}><Trans>Date</Trans></Label>
        <Datetime
          className="t-date-selector"
          closeOnSelect
          dateFormat="L"
          inputProps={{ className: `bf-input bf-input--primary${displayDateError ? ' invalid' : ''}`, id: dateSelectorId}}
          isValidDate={generatePickerDateValidationFunction()}
          onBlur={handleBlurDate}
          onChange={handleUpdateDate}
          timeFormat={false}
          value={selectedTimestamp}
        />
        <span className="bff-calendar icon" />
      </div>
      {showTime && (
        <div className="date-time-picker__time date-time-picker__container">
          <Label attributes={{ htmlFor: timeSelectorId }}><Trans>Time</Trans></Label>
          <Datetime
            closeOnSelect
            dateFormat={false}
            inputProps={{ className: `bf-input bf-input--primary${displayTimeError ? ' invalid' : ''}`, id: timeSelectorId }}
            onBlur={handleBlurTime}
            onChange={handleUpdateTime}
            timeFormat="h:mm a"
            value={selectedTimestamp}
          />
        </div>
      )}
      {showTimezones && (
        <div className="date-time-picker__timezone date-time-picker__container">
          <Label attributes={{ htmlFor: timezoneSelectorId, id: `${timezoneSelectorId}_label` }}><Trans>Timezone</Trans></Label>
          <ListDropdown
            aria-labelledby={`${timezoneSelectorId}_label`}
            id={timezoneSelectorId}
            onChange={(selection) => setSelectedTimezone(selection ? selection.value : defaultTimezone)}
            options={timezoneOptions}
            value={selectedTimezone}
            virtualizeOptions={false}
          />
        </div>
      )}
    </div>
  );
};

DateTimePicker.propTypes = {
  allowPastDates: PropTypes.bool,
  className: PropTypes.string,
  maxDaysInFuture: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onDateValidityChange: PropTypes.func,
  showTime: PropTypes.bool,
  showTimezones: PropTypes.bool,
  timestamp: PropTypes.any, // eslint-disable-line
};

DateTimePicker.defaultProps = {
  allowPastDates: false,
  className: '',
  maxDaysInFuture: null,
  onDateValidityChange: () => {},
  showTime: true,
  showTimezones: true,
  timestamp: moment().locale(momentLocale),
};

export default DateTimePicker;
